import type { BalancePostingWrapperCollection } from '@festina/life-general-types/life'
import { isBalancePosting } from '@festina/moneyin-general-types/moneyin'
import { dateUtils, notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import { CardItem, CardItems, SimpleModal } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { CreatedBy } from '../../components/userDetails/CreatedBy'
import { useHook } from '../../lenteHooks'
import { BalancePostingsCardTable } from './BalancePostingsCardTable'

type Props = {
  workPadId: string
  onClose: () => void
}

const WorkpadBalancePostingsModalInternal = ({ workPadId, onClose }: Props) => {
  const translate = useTranslate()

  const tenantId = useHook('routing.tenant.useTenantParam')()

  const { data: workPad } = useHook('fetchers.life.useFetchWorkPad')({
    tenantId,
    workPadId,
  })

  const filteredPostings: BalancePostingWrapperCollection['balancePostingWrappers'] =
    useMemo(
      () =>
        workPad?.postingLegs
          ?.flatMap(
            (postingLeg) =>
              postingLeg?.postings
                ?.filter(isBalancePosting)
                .map((balancePosting) => ({
                  _type: 'BalancePostingWrapper' as const,
                  balancePosting,
                })) ?? [],
          )
          .filter(notEmpty) ?? [],
      [workPad],
    )

  return (
    <SimpleModal
      isOpen
      title={translate('workPad.title')}
      closeConfiguration={{
        label: translate('close'),
        onClose,
      }}
    >
      <CardItems>
        <CardItem
          title={translate('workPad.creationStamp')}
          value={dateUtils.dateWithTimeAsStr(workPad?.creationStamp)}
        />
        <CardItem
          title={translate('workPad.userId')}
          value={workPad?.userId ? <CreatedBy userId={workPad.userId} /> : '-'}
        />
      </CardItems>
      <BalancePostingsCardTable
        balancePostings={{
          _type: 'BalancePostingWrapperCollection',
          balancePostingWrappers: filteredPostings,
        }}
      />
    </SimpleModal>
  )
}

export const WorkpadBalancePostingsModal = withSuspense(
  WorkpadBalancePostingsModalInternal,
)
